import { AfterViewInit, Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CatalogLocationService, CountryOption, RegionOption } from '../shared/catalog-location.service';
import { CatalogCrudModalComponent } from '../shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../shared/components/catalog-delete-confirm-modal';
import { CatalogPageCardComponent } from '../shared/components/catalog-page-card';
import { CatalogTableToolbarComponent } from '../shared/components/catalog-table-toolbar';
import { CatalogDatatableController, DatatableViewStateSnapshot } from '../shared/catalog-datatable.controller';
import { CatalogModalController } from '../shared/catalog-modal.controller';
import {
  buildDocumentUploadToast,
  createDocumentUploadEntries,
  DocumentUploadEntry,
  summarizeDocumentUploads,
  updateDocumentUploadEntry,
  uploadDocumentsSequentially,
} from '../../shared/document-upload';
import { DocumentUploadList } from '../../shared/document-upload-list';
import { DocumentNameCell } from '../../shared/document-name-cell';
import { DocumentDescriptionCell } from '../../shared/document-description-cell';
import { resolveApiErrorMessage } from '../../shared/api-error';
import {
  CreateInvestorGroupRequest,
  DocumentCreateRequest,
  DocumentUpdateRequest,
  InvestorGroup,
  InvestorGroupDocumentItem,
  InvestorGroupNoteItem,
  NoteUpsertRequest,
  UpdateInvestorGroupRequest,
} from './investor-group.types';
import { InvestorGroupService } from './investor-group.service';

interface InvestorGroupFormValue {
  name: string;
  hqCountryId: string;
  regionId: string;
}

interface InvestorGroupDocumentFormValue {
  description: string;
  filename: string;
  files: File[];
}

interface InvestorGroupNoteFormValue {
  note: string;
}

type InvestorGroupFormControlName = keyof InvestorGroupFormValue;

@Component({
  selector: 'app-investor-group',
  imports: [
    ReactiveFormsModule,
    CatalogPageCardComponent,
    CatalogTableToolbarComponent,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
    DocumentUploadList,
    DocumentNameCell,
    DocumentDescriptionCell,
  ],
  templateUrl: './investor-group.html',
  styleUrl: './investor-group.css',
})
export class InvestorGroupComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly nameMaxLength = 60;
  private readonly datatableId = 'kt_investor_group_datatable';
  private readonly createModalId = 'create_investor_group_modal';
  private readonly editModalId = 'edit_investor_group_modal';
  private readonly deleteConfirmModalId = 'delete_investor_group_confirm_modal';
  private readonly destroyRef = inject(DestroyRef);
  private readonly createSearchableSelectIds = ['create-investor-group-country'];
  private readonly editSearchableSelectIds = ['edit-investor-group-country'];
  private readonly notesSource = 'InvestorGroup';
  private readonly documentSource = 'InvestorGroup';
  private readonly allowedDocumentTypes = ['.doc', '.msg', '.pdf', '.xls'];
  private readonly formBuilder = inject(FormBuilder);
  private readonly datatableController = new CatalogDatatableController(this.datatableId);
  private readonly modalController = new CatalogModalController();

  private createModalObserver?: MutationObserver;
  private editModalObserver?: MutationObserver;

  protected readonly createForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    hqCountryId: [''],
    regionId: [''],
  });
  protected readonly editForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    hqCountryId: [''],
    regionId: [''],
  });
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly rows = signal<InvestorGroup[]>([]);
  protected readonly countries = signal<CountryOption[]>([]);
  protected readonly regions = signal<RegionOption[]>([]);
  protected readonly lookupError = signal('');
  protected readonly isCreating = signal(false);
  protected readonly createFormSubmitted = signal(false);
  protected readonly createFormError = signal('');
  protected readonly editingId = signal<number | null>(null);
  protected readonly isUpdating = signal(false);
  protected readonly editFormSubmitted = signal(false);
  protected readonly editFormError = signal('');
  protected readonly deletingId = signal<number | null>(null);
  protected readonly pendingDeleteId = signal<number | null>(null);
  protected readonly alertVariant = signal<'success' | 'destructive' | null>(null);
  protected readonly alertTitle = signal('');
  protected readonly alertMessage = signal('');
  protected readonly documents = signal<InvestorGroupDocumentItem[]>([]);
  protected readonly notes = signal<InvestorGroupNoteItem[]>([]);
  protected readonly isMutatingDocument = signal(false);
  protected readonly renamingDocumentId = signal<number | null>(null);
  protected readonly describingDocumentId = signal<number | null>(null);
  protected readonly documentUploadEntries = signal<DocumentUploadEntry[]>([]);
  protected readonly isMutatingNote = signal(false);
  protected readonly documentFormError = signal('');
  protected readonly noteFormError = signal('');
  protected readonly editingDocumentId = signal<number | null>(null);
  protected readonly editingNoteId = signal<number | null>(null);
  protected readonly deletingDocumentId = signal<number | null>(null);
  protected readonly deletingNoteId = signal<number | null>(null);
  protected readonly isDocumentEditorVisible = signal(false);
  protected readonly isNoteEditorVisible = signal(false);
  protected readonly documentForm = signal<InvestorGroupDocumentFormValue>({
    description: '',
    filename: '',
    files: [],
  });
  protected readonly noteForm = signal<InvestorGroupNoteFormValue>({
    note: '',
  });
  protected readonly documentModalTitle = computed(() => this.editingDocumentId() === null ? 'Upload Documentation' : 'Edit Documentation');
  protected readonly documentModalSubmitLabel = computed(() => this.editingDocumentId() === null ? 'Upload Documents' : 'Save Changes');
  protected readonly noteModalTitle = computed(() => this.editingNoteId() === null ? 'Add Note' : 'Edit Note');
  protected readonly noteModalSubmitLabel = computed(() => this.editingNoteId() === null ? 'Add Note' : 'Save Changes');
  protected readonly hasAlert = computed(() => this.alertVariant() !== null);

  constructor(
    private readonly investorGroupService: InvestorGroupService,
    private readonly catalogLocationService: CatalogLocationService,
  ) {}

  ngOnInit(): void {
    this.loadRows();
    this.loadLookups();
    this.bindCountryRegionAutoSelect();
  }

  ngAfterViewInit(): void {
    this.bindCreateModalCloseReset();
    this.bindEditModalCloseReset();
  }

  ngOnDestroy(): void {
    this.createModalObserver?.disconnect();
    this.editModalObserver?.disconnect();
  }

  protected shouldShowCreateFieldError(controlName: InvestorGroupFormControlName): boolean {
    return this.createFormSubmitted() && this.createForm.controls[controlName].invalid;
  }

  protected shouldShowEditFieldError(controlName: InvestorGroupFormControlName): boolean {
    return this.editFormSubmitted() && this.editForm.controls[controlName].invalid;
  }

  protected shouldShowCreateRequiredError(controlName: InvestorGroupFormControlName): boolean {
    const control = this.createForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowCreateMaxLengthError(controlName: InvestorGroupFormControlName): boolean {
    const control = this.createForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected shouldShowEditRequiredError(controlName: InvestorGroupFormControlName): boolean {
    const control = this.editForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowEditMaxLengthError(controlName: InvestorGroupFormControlName): boolean {
    const control = this.editForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected loadRows(snapshot?: DatatableViewStateSnapshot): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.investorGroupService.getInvestorGroups().subscribe({
      next: (rows) => {
        this.rows.set(rows);
        this.isLoading.set(false);
        this.datatableController.initialize(snapshot);
      },
      error: () => {
        this.errorMessage.set('Unable to load investor group data from the API.');
        this.isLoading.set(false);
      },
    });
  }

  protected onCreate(): void {
    this.createFormSubmitted.set(true);
    this.createFormError.set('');

    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const payload: CreateInvestorGroupRequest = {
      ...this.toPayloadDraft(this.createForm.getRawValue()),
      createdBy: '',
    };

    this.isCreating.set(true);

    this.investorGroupService.createInvestorGroup(payload).subscribe({
      next: () => {
        this.isCreating.set(false);
        this.closeCreateModal();
        this.resetCreateForm();
        window.KTToast?.show({
          message: 'Investor group created successfully.',
          variant: 'success',
        });
        this.loadRows();
      },
      error: () => {
        this.isCreating.set(false);
        this.createFormError.set('Unable to create investor group at this time. Please try again.');
      },
    });
  }

  protected resetCreateForm(): void {
    this.createForm.reset(this.getEmptyFormValue());
    this.createFormSubmitted.set(false);
    this.createFormError.set('');
    this.documents.set([]);
    this.notes.set([]);
  }

  protected openEditModal(row: InvestorGroup): void {
    this.editingId.set(row.id);
    this.editForm.reset({
      name: row.name,
      hqCountryId: this.toSelectValue(row.hqCountryId),
      regionId: this.toSelectValue(row.regionId),
    });
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.resetDocumentForm();
    this.resetNoteForm();
    this.isDocumentEditorVisible.set(false);
    this.isNoteEditorVisible.set(false);

    this.syncEditRegionWithCountry(this.editForm.controls.hqCountryId.value);
    this.refreshSelectUiBatch(this.editSearchableSelectIds);
    this.initializeKtSelects();
    this.loadRelatedData(row.id);
    this.modalController.open(this.editModalId);
  }

  protected onUpdate(): void {
    this.editFormSubmitted.set(true);
    this.editFormError.set('');

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const id = this.editingId();
    if (id === null) {
      this.editFormError.set('Unable to identify the investor group to update.');
      return;
    }

    const payload: UpdateInvestorGroupRequest = {
      ...this.toPayloadDraft(this.editForm.getRawValue()),
      modifiedBy: '',
    };
    const datatableSnapshot = this.datatableController.captureViewState();

    this.isUpdating.set(true);

    this.investorGroupService.updateInvestorGroup(id, payload).subscribe({
      next: () => {
        this.isUpdating.set(false);
        this.closeEditModal();
        this.resetEditForm();
        window.KTToast?.show({
          message: 'Investor group updated successfully.',
          variant: 'success',
        });
        this.loadRows(datatableSnapshot);
      },
      error: () => {
        this.isUpdating.set(false);
        this.editFormError.set('Unable to update investor group at this time. Please try again.');
      },
    });
  }

  protected resetEditForm(): void {
    this.editForm.reset(this.getEmptyFormValue());
    this.editingId.set(null);
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.documents.set([]);
    this.notes.set([]);
    this.isDocumentEditorVisible.set(false);
    this.isNoteEditorVisible.set(false);
  }

  protected trackDocument(_: number, item: InvestorGroupDocumentItem): string {
    return `${item.id}-${item.filename}-${item.description}`;
  }

  protected trackNote(_: number, item: InvestorGroupNoteItem): string {
    return `${item.id}-${item.dateModified}`;
  }

  protected resetDocumentForm(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.documentFormError.set('');
    this.editingDocumentId.set(null);
    this.documentUploadEntries.set([]);
    this.documentForm.set({
      description: '',
      filename: '',
      files: [],
    });
  }

  protected openCreateDocumentEditor(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.resetDocumentForm();
    this.isDocumentEditorVisible.set(true);
  }

  protected closeDocumentEditor(): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.resetDocumentForm();
    this.isDocumentEditorVisible.set(false);
  }

  protected documentFilenames(): string[] {
    return this.documents().map((document) => document.filename);
  }

  protected renameDocument(document: InvestorGroupDocumentItem, filename: string): void {
    this.saveDocumentInline(document, 'filename', filename);
  }

  protected updateDocumentDescription(document: InvestorGroupDocumentItem, description: string | null): void {
    this.saveDocumentInline(document, 'description', description);
  }

  private saveDocumentInline(document: InvestorGroupDocumentItem, field: 'filename' | 'description', value: string | null): void {
    const parentId = this.currentParentId();
    if (!parentId || this.isMutatingDocument()) {
      return;
    }

    const payload: DocumentUpdateRequest = {
      parentElementId: parentId,
      securityId: null,
      description: field === 'description' ? value : document.description || null,
      type: document.type || null,
      filename: field === 'filename' ? (value ?? document.filename) : document.filename,
      ezShareId: document.ezShareId || null,
      ezShareLink: document.ezShareLink || null,
      source: this.documentSource,
    };

    this.isMutatingDocument.set(true);
    const savingDocumentId = field === 'filename' ? this.renamingDocumentId : this.describingDocumentId;
    savingDocumentId.set(document.id);
    this.investorGroupService.updateDocument(document.id, payload).subscribe({
      next: () => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        this.loadDocuments(parentId);
        window.KTToast?.show({
          message: field === 'filename' ? `Document renamed to '${value}'.` : 'Document description updated.',
          variant: 'success',
        });
      },
      error: (error: unknown) => {
        this.isMutatingDocument.set(false);
        savingDocumentId.set(null);
        window.KTToast?.show({
          message: resolveApiErrorMessage(
            field === 'filename'
              ? 'Unable to rename document. Please try again.'
              : 'Unable to update document description. Please try again.',
            error,
          ),
          variant: 'destructive',
        });
      },
    });
  }

  protected startEditDocumentRow(row: InvestorGroupDocumentItem): void {
    if (this.isMutatingDocument()) {
      return;
    }

    this.documentFormError.set('');
    this.editingDocumentId.set(row.id);
    this.documentForm.set({
      description: row.description,
      filename: row.filename,
      files: [],
    });
    this.isDocumentEditorVisible.set(true);
  }

  protected updateDocumentField(field: 'description' | 'filename', value: string): void {
    this.documentForm.update((current) => ({
      ...current,
      [field]: value,
    }));
    this.documentFormError.set('');
  }

  protected onDocumentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];

    this.documentForm.update((current) => ({
      ...current,
      files,
    }));
    this.documentUploadEntries.set(createDocumentUploadEntries(files));
    this.documentFormError.set('');
  }

  protected addOrUpdateDocument(): void {
    const parentId = this.currentParentId();
    if (!parentId || this.isMutatingDocument()) {
      this.documentFormError.set('Save the investor group first to manage documentation.');
      return;
    }

    const form = this.documentForm();
    const editingDocumentId = this.editingDocumentId();
    if (editingDocumentId !== null) {
      const filename = form.filename.trim();
      if (!filename) {
        this.documentFormError.set('Filename is required.');
        return;
      }

      const currentDocument = this.documents().find((document) => document.id === editingDocumentId);
      if (!currentDocument) {
        this.documentFormError.set('Unable to identify the document to update.');
        return;
      }

      const payload: DocumentUpdateRequest = {
        parentElementId: parentId,
        securityId: null,
        description: form.description.trim() || null,
        type: currentDocument.type || null,
        filename,
        ezShareId: currentDocument.ezShareId || null,
        ezShareLink: currentDocument.ezShareLink || null,
        source: this.documentSource,
      };

      this.isMutatingDocument.set(true);
      this.documentFormError.set('');
      this.investorGroupService.updateDocument(editingDocumentId, payload).subscribe({
        next: () => {
          this.isMutatingDocument.set(false);
          this.resetDocumentForm();
          this.isDocumentEditorVisible.set(false);
          this.loadDocuments(parentId);
          window.KTToast?.show({ message: 'Document metadata updated successfully.', variant: 'success' });
        },
        error: () => {
          this.isMutatingDocument.set(false);
          this.documentFormError.set('Unable to update document. Please try again.');
        },
      });
      return;
    }

    if (form.files.length === 0) {
      this.documentFormError.set('Select at least one file to upload.');
      return;
    }

    const reservedFilenames = new Set(
      this.documents()
        .map((document) => document.filename.trim().toUpperCase())
        .filter((filename) => filename.length > 0),
    );
    const invalidFiles: string[] = [];
    const payloads: DocumentCreateRequest[] = [];

    for (const file of form.files) {
      const extension = this.extractFileExtension(file.name);
      if (!extension || !this.allowedDocumentTypes.includes(extension)) {
        invalidFiles.push(file.name || '(unknown file)');
        continue;
      }

      let generatedFilename = '';
      let attempts = 0;
      do {
        generatedFilename = `${this.generateDocumentCode()}.${extension.slice(1)}`.toUpperCase();
        attempts += 1;
      } while (reservedFilenames.has(generatedFilename) && attempts < 10);

      if (reservedFilenames.has(generatedFilename)) {
        this.documentFormError.set('Unable to generate a unique filename code. Please retry upload.');
        return;
      }
      reservedFilenames.add(generatedFilename);

      payloads.push({
        filename: generatedFilename,
        parentElementId: parentId,
        securityId: null,
        description: '',
        type: extension,
        source: this.documentSource,
        modifiedBy: this.resolveModifiedBy(),
        file,
      });
    }

    if (invalidFiles.length > 0) {
      this.documentFormError.set(
        `Unsupported file type: ${invalidFiles.join(', ')}. Allowed types: ${this.allowedDocumentTypes.join(', ')}.`,
      );
      return;
    }

    this.isMutatingDocument.set(true);
    this.documentFormError.set('');
    uploadDocumentsSequentially(
      payloads,
      (payload) => this.investorGroupService.createDocument(payload),
      (file, status, message) =>
        this.documentUploadEntries.update((entries) => updateDocumentUploadEntry(entries, file, status, message)),
    ).subscribe((outcomes) => {
      const summary = summarizeDocumentUploads(outcomes);
      this.isMutatingDocument.set(false);
      this.loadDocuments(parentId);
      window.KTToast?.show(buildDocumentUploadToast(summary));

      if (summary.failed.length === 0) {
        this.resetDocumentForm();
        this.isDocumentEditorVisible.set(false);
        return;
      }

      this.documentForm.update((current) => ({ ...current, files: summary.retryableFiles }));
      if (summary.retryableFiles.length > 0) {
        this.documentFormError.set('Some files could not be uploaded. Upload again to retry only the failed files.');
      }
    });
  }

  protected requestDeleteDocumentRow(row: InvestorGroupDocumentItem): void {
    const documentId = row.id;
    const parentId = this.currentParentId();
    if (!parentId || this.isMutatingDocument()) {
      return;
    }

    this.isMutatingDocument.set(true);
    this.deletingDocumentId.set(documentId);
    this.documentFormError.set('');
    this.investorGroupService.deleteDocument(documentId).subscribe({
      next: () => {
        this.isMutatingDocument.set(false);
        this.deletingDocumentId.set(null);
        this.loadDocuments(parentId);
        window.KTToast?.show({ message: 'Document deleted successfully.', variant: 'success' });
      },
      error: () => {
        this.isMutatingDocument.set(false);
        this.deletingDocumentId.set(null);
        this.documentFormError.set('Unable to delete document. Please try again.');
      },
    });
  }

  protected resetNoteForm(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.noteFormError.set('');
    this.editingNoteId.set(null);
    this.noteForm.set({ note: '' });
  }

  protected openCreateNoteEditor(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.resetNoteForm();
    this.isNoteEditorVisible.set(true);
  }

  protected closeNoteEditor(): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.resetNoteForm();
    this.isNoteEditorVisible.set(false);
  }

  protected startEditNoteRow(row: InvestorGroupNoteItem): void {
    if (this.isMutatingNote()) {
      return;
    }

    this.noteFormError.set('');
    this.editingNoteId.set(row.id);
    this.noteForm.set({ note: row.description });
    this.isNoteEditorVisible.set(true);
  }

  protected updateNoteField(value: string): void {
    this.noteForm.set({ note: value });
    this.noteFormError.set('');
  }

  protected addOrUpdateNote(): void {
    const parentId = this.currentParentId();
    if (!parentId || this.isMutatingNote()) {
      this.noteFormError.set('Save the investor group first to manage notes.');
      return;
    }

    const noteText = this.noteForm().note.trim();
    if (!noteText) {
      this.noteFormError.set('Note text is required.');
      return;
    }

    if (noteText.length > 1600) {
      this.noteFormError.set('Note text cannot exceed 1600 characters.');
      return;
    }

    const payload: NoteUpsertRequest = {
      parentId,
      source: this.notesSource,
      description: noteText,
      modifiedBy: this.resolveModifiedBy(),
    };

    const editingNoteId = this.editingNoteId();
    this.isMutatingNote.set(true);
    this.noteFormError.set('');
    if (editingNoteId === null) {
      this.investorGroupService.createNote(payload).subscribe({
        next: () => {
          this.isMutatingNote.set(false);
          this.resetNoteForm();
          this.isNoteEditorVisible.set(false);
          this.loadNotes(parentId);
          window.KTToast?.show({ message: 'Note added successfully.', variant: 'success' });
        },
        error: () => {
          this.isMutatingNote.set(false);
          this.noteFormError.set('Unable to add note. Please try again.');
        },
      });
      return;
    }

    this.investorGroupService.updateNote(editingNoteId, payload).subscribe({
      next: () => {
        this.isMutatingNote.set(false);
        this.resetNoteForm();
        this.isNoteEditorVisible.set(false);
        this.loadNotes(parentId);
        window.KTToast?.show({ message: 'Note updated successfully.', variant: 'success' });
      },
      error: () => {
        this.isMutatingNote.set(false);
        this.noteFormError.set('Unable to update note. Please try again.');
      },
    });
  }

  protected requestDeleteNoteRow(row: InvestorGroupNoteItem): void {
    const noteId = row.id;
    const parentId = this.currentParentId();
    if (!parentId || this.isMutatingNote()) {
      return;
    }

    this.isMutatingNote.set(true);
    this.deletingNoteId.set(noteId);
    this.noteFormError.set('');
    this.investorGroupService.deleteNote(noteId).subscribe({
      next: () => {
        this.isMutatingNote.set(false);
        this.deletingNoteId.set(null);
        this.loadNotes(parentId);
        window.KTToast?.show({ message: 'Note deleted successfully.', variant: 'success' });
      },
      error: () => {
        this.isMutatingNote.set(false);
        this.deletingNoteId.set(null);
        this.noteFormError.set('Unable to delete note. Please try again.');
      },
    });
  }

  protected onTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const editButton = target?.closest('button[data-action="edit-investor-group"]') as HTMLButtonElement | null;
    const deleteButton = target?.closest('button[data-action="delete-investor-group"]') as HTMLButtonElement | null;

    if (editButton && !editButton.disabled) {
      const rowId = this.getRowIdFromButton(editButton);
      if (rowId === null) {
        return;
      }

      const row = this.rows().find((item) => item.id === rowId);
      if (!row) {
        return;
      }

      this.openEditModal(row);
      return;
    }

    if (!deleteButton || deleteButton.disabled) {
      return;
    }

    const rowId = this.getRowIdFromButton(deleteButton);
    if (rowId === null) {
      return;
    }

    this.openDeleteConfirmationModal(rowId);
  }

  protected cancelDelete(): void {
    this.pendingDeleteId.set(null);
    this.closeDeleteConfirmationModal();
  }

  protected confirmDelete(): void {
    const rowId = this.pendingDeleteId();

    if (rowId === null || this.deletingId() !== null) {
      return;
    }

    this.closeDeleteConfirmationModal();
    this.pendingDeleteId.set(null);
    this.removeRow(rowId);
  }

  protected removeRow(rowId: number): void {
    if (this.deletingId() !== null) {
      return;
    }

    this.deletingId.set(rowId);

    this.investorGroupService.deleteInvestorGroup(rowId).subscribe({
      next: (result) => {
        this.deletingId.set(null);

        if (result.kind === 'success') {
          const snapshot = this.datatableController.captureViewState();
          this.rows.update((rows) => rows.filter((item) => item.id !== rowId));
          this.showSuccessAlert('Investor group deleted successfully.');
          this.datatableController.initialize(snapshot);
          return;
        }

        this.showDestructiveAlert(
          'Unable to delete investor group.',
          result.message,
        );
      },
      error: () => {
        this.deletingId.set(null);
        this.showDestructiveAlert(
          'Unable to delete investor group.',
          'Unable to delete investor group at this time. Please try again.',
        );
      },
    });
  }

  protected dismissAlert(): void {
    this.alertVariant.set(null);
    this.alertTitle.set('');
    this.alertMessage.set('');
  }

  protected formatDateTime(value: string): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  private loadRelatedData(parentIdNumber: number): void {
    const parentId = String(parentIdNumber);
    this.loadDocuments(parentId);
    this.loadNotes(parentId);
  }

  private loadDocuments(parentId: string): void {
    this.investorGroupService.getDocumentsByParent(parentId).subscribe({
      next: (documents) => {
        const source = this.documentSource.toUpperCase();
        const filtered = documents.filter((item) => {
          return item.parentElementId.trim() === parentId
            && (!item.source.trim() || item.source.trim().toUpperCase() === source);
        });
        this.documents.set(filtered);
      },
      error: () => {
        this.documents.set([]);
      },
    });
  }

  private loadNotes(parentId: string): void {
    this.investorGroupService.getNotes().subscribe({
      next: (notes) => {
        const source = this.notesSource.toUpperCase();
        const filtered = notes.filter((item) => {
          return item.parentId.trim() === parentId
            && item.source.trim().toUpperCase() === source;
        });
        this.notes.set(filtered);
      },
      error: () => {
        this.notes.set([]);
      },
    });
  }

  private loadLookups(): void {
    this.lookupError.set('');

    this.catalogLocationService.getCountries().subscribe({
      next: (rows) => {
        this.countries.set(rows);
        this.syncCreateRegionWithCountry(this.createForm.controls.hqCountryId.value);
        this.syncEditRegionWithCountry(this.editForm.controls.hqCountryId.value);
        this.initializeKtSelects();
      },
      error: () => {
        this.lookupError.set('Unable to load country options.');
      },
    });

    this.catalogLocationService.getRegions().subscribe({
      next: (rows) => {
        this.regions.set(rows);
      },
      error: () => {
        this.lookupError.set('Unable to load region options.');
      },
    });
  }

  private bindCountryRegionAutoSelect(): void {
    this.createForm.controls.hqCountryId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countryIdValue) => {
        this.syncCreateRegionWithCountry(countryIdValue);
      });

    this.editForm.controls.hqCountryId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countryIdValue) => {
        this.syncEditRegionWithCountry(countryIdValue);
      });
  }

  private syncCreateRegionWithCountry(countryIdValue: string): void {
    this.syncRegionControlWithCountry(countryIdValue, this.createForm.controls.regionId, 'create-investor-group-region');
  }

  private syncEditRegionWithCountry(countryIdValue: string): void {
    this.syncRegionControlWithCountry(countryIdValue, this.editForm.controls.regionId, 'edit-investor-group-region');
  }

  private syncRegionControlWithCountry(
    countryIdValue: string,
    regionControl: FormControl<string>,
    regionSelectId: string,
  ): void {
    const countryId = Number(countryIdValue);
    const selectedCountry = Number.isFinite(countryId)
      ? this.countries().find((item) => item.id === countryId)
      : undefined;

    regionControl.setValue(
      selectedCountry && selectedCountry.regionId !== null
        ? String(selectedCountry.regionId)
        : '',
    );

    this.refreshSelectUi(regionSelectId);
  }

  private initializeKtSelects(): void {
    requestAnimationFrame(() => {
      window.KTSelect?.createInstances();
    });
  }

  private bindCreateModalCloseReset(): void {
    this.createModalObserver = this.modalController.observeClose(this.createModalId, () => {
      this.handleCreateModalClosed();
    });
  }

  private bindEditModalCloseReset(): void {
    this.editModalObserver = this.modalController.observeClose(this.editModalId, () => {
      this.handleEditModalClosed();
    });
  }

  private handleCreateModalClosed(): void {
    this.resetCreateForm();
    this.clearSelectUiState(this.createSearchableSelectIds);
  }

  private handleEditModalClosed(): void {
    this.resetEditForm();
    this.clearSelectUiState(this.editSearchableSelectIds);
  }

  private clearSelectUiState(selectIds: readonly string[]): void {
    selectIds.forEach((selectId) => {
      const selectElement = document.getElementById(selectId) as HTMLSelectElement | null;
      if (!selectElement) {
        return;
      }

      selectElement.value = '';
      selectElement.dispatchEvent(new Event('change', { bubbles: true }));

      const instance = window.KTSelect?.getInstance?.(selectElement) as KTSelectInstance | undefined;
      instance?.showAllOptions?.();
      instance?.closeDropdown?.();
      instance?.refresh?.();
    });

    this.initializeKtSelects();
  }

  private refreshSelectUiBatch(selectIds: readonly string[]): void {
    selectIds.forEach((selectId) => {
      this.refreshSelectUi(selectId);
    });
  }

  private refreshSelectUi(selectId: string): void {
    const selectElement = document.getElementById(selectId) as HTMLSelectElement | null;
    if (!selectElement) {
      return;
    }

    selectElement.dispatchEvent(new Event('change', { bubbles: true }));
    const instance = window.KTSelect?.getInstance?.(selectElement) as KTSelectInstance | undefined;
    instance?.refresh?.();
  }

  private closeCreateModal(): void {
    this.modalController.close(this.createModalId);
  }

  private closeEditModal(): void {
    this.modalController.close(this.editModalId);
  }

  private openDeleteConfirmationModal(rowId: number): void {
    this.pendingDeleteId.set(rowId);
    this.modalController.open(this.deleteConfirmModalId);
  }

  private closeDeleteConfirmationModal(): void {
    this.modalController.close(this.deleteConfirmModalId);
  }

  private showSuccessAlert(message: string): void {
    this.alertVariant.set('success');
    this.alertTitle.set(message);
    this.alertMessage.set('');
  }

  private showDestructiveAlert(title: string, message: string): void {
    this.alertVariant.set('destructive');
    this.alertTitle.set(title);
    this.alertMessage.set(message);
  }

  private getRowIdFromButton(button: HTMLButtonElement): number | null {
    const rowIdRaw = button.getAttribute('data-investor-group-id');
    const rowId = rowIdRaw ? Number(rowIdRaw) : Number.NaN;

    return Number.isFinite(rowId) ? rowId : null;
  }

  private getEmptyFormValue(): InvestorGroupFormValue {
    return {
      name: '',
      hqCountryId: '',
      regionId: '',
    };
  }

  private toPayloadDraft(rawValue: InvestorGroupFormValue): Omit<CreateInvestorGroupRequest, 'createdBy'> {
    return {
      name: rawValue.name.trim(),
      hqCountryId: this.parseNullableId(rawValue.hqCountryId),
      regionId: this.parseNullableId(rawValue.regionId),
    };
  }

  private parseNullableId(rawId: string): number | null {
    const parsedId = Number(rawId);

    return Number.isFinite(parsedId) && parsedId > 0
      ? parsedId
      : null;
  }

  private toSelectValue(value: number | null): string {
    return typeof value === 'number' && Number.isFinite(value) && value > 0
      ? String(value)
      : '';
  }

  private currentParentId(): string | null {
    const id = this.editingId();
    if (id === null) {
      return null;
    }

    return String(id);
  }

  private resolveModifiedBy(): string {
    return 'FRIDA UI';
  }

  private extractFileExtension(filename: string): string | null {
    const trimmed = filename.trim();
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === trimmed.length - 1) {
      return null;
    }

    return trimmed.slice(lastDot).toLowerCase();
  }

  private generateDocumentCode(length = 12): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let index = 0; index < length; index += 1) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      result += charset[randomIndex];
    }

    return result;
  }
}
